import {
  calculateCPC,
  calculateCPM,
  calculateCPX,
  calculateCTR,
  calculateROAS
} from '@src/utils/metric-utils';
import {
  AdSetStatus,
  FatiguePoint,
  LaunchEvent,
  PerfDailyPoint,
  PlatformSpread,
  Ad,
  AdSet,
  FatiguedAd
} from './meta-fatigue-insights-constants';
import { computeConcentration, computePlatformSpread, round } from './meta-fatigue-insights-utils';
import {
  AdKpiVolumeRevenueRow,
  AdPerformanceTotalsRow,
  AdSetDailyPerformanceRow,
  AdSetPerformanceTotalsRow,
  AdsetPlacementBreakdownRow,
  AdsetStatusRow,
  CreativeFatigueSummaryRow,
  CreativeFatiguedAdsRow,
  DiversityLabelScoreRow
} from './meta-fatigue-types';

interface Agg {
  spend: number;
  impressions: number;
  clicks: number;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

interface ComputedMetrics extends Agg {
  cpm: number | null;
  ctr: number | null;
  cpc: number | null;
}

const computeMetrics = (agg: Agg): ComputedMetrics => {
  const { spend, impressions, clicks } = agg;
  return {
    spend: round(spend, 2),
    impressions,
    clicks,
    cpm: impressions > 0 ? round(calculateCPM(spend, impressions)) : null,
    ctr: impressions > 0 ? round(calculateCTR(clicks, impressions)) : null,
    cpc: clicks > 0 ? round(calculateCPC(spend, clicks)) : null
  };
};

const computeRoas = (kpiRevenue: number | null, spend: number): number | null =>
  kpiRevenue !== null && kpiRevenue > 0 && spend > 0
    ? round(calculateROAS(kpiRevenue, spend))
    : null;

const computeCpx = (kpiVolume: number | null, spend: number): number | null =>
  kpiVolume !== null && kpiVolume > 0 ? round(calculateCPX(spend, kpiVolume), 2) : null;

interface LiveDataInputs {
  adSetTotals: AdSetPerformanceTotalsRow[];
  adTotals: AdPerformanceTotalsRow[];
  adSetDailyPerf: AdSetDailyPerformanceRow[];
  diversity: DiversityLabelScoreRow[];
  fatigueSummary: CreativeFatigueSummaryRow[];
  fatiguedAds: CreativeFatiguedAdsRow[];
  adsetStatus: AdsetStatusRow[];
  placementBreakdown: AdsetPlacementBreakdownRow[];
  adKpiVolumeRevenue: AdKpiVolumeRevenueRow[];
}

export interface LiveAdSetsResult {
  adSets: AdSet[];
  campaigns: string[];
}

const VALID_LABELS = new Set(['LOW', 'MEDIUM', 'HIGH']);

export const buildAdSetsFromLiveData = ({
  adSetTotals,
  adTotals,
  adSetDailyPerf,
  diversity,
  fatigueSummary,
  fatiguedAds,
  adsetStatus,
  placementBreakdown,
  adKpiVolumeRevenue
}: LiveDataInputs): LiveAdSetsResult => {
  const kpiByAdId = new Map<string, { volume: number; revenue: number }>(
    adKpiVolumeRevenue.map(r => [r.ad_id, { volume: num(r.volume), revenue: num(r.revenue) }])
  );
  const placementsBySet = new Map<string, { publisher: string; position: string }[]>();
  for (const r of placementBreakdown) {
    const list = placementsBySet.get(r.adset_id) ?? [];
    list.push({ publisher: r.publisher_platform, position: r.platform_position });
    placementsBySet.set(r.adset_id, list);
  }
  const platformsBySet = new Map<string, PlatformSpread>(
    [...placementsBySet].map(([adsetId, placements]) => [
      adsetId,
      computePlatformSpread(placements)
    ])
  );

  const diversityBySet = new Map<string, { label: 'LOW' | 'MEDIUM' | 'HIGH'; score: number }>();
  for (const r of diversity) {
    const label = (r.creative_diversity_label ?? '').trim().toUpperCase();
    const rawScore = r.creative_diversity_score;
    if (!VALID_LABELS.has(label) || rawScore === null || rawScore === '') continue;
    const score = Number(rawScore);
    if (!Number.isFinite(score)) continue;
    diversityBySet.set(r.adset_id, {
      label: label as 'LOW' | 'MEDIUM' | 'HIGH',
      score: round(score)
    });
  }

  const fatigueBySet = new Map<string, FatiguePoint[]>();
  for (const r of fatigueSummary) {
    const list = fatigueBySet.get(r.ad_object_id) ?? [];
    list.push({ date: r.date, fatigue: round(num(r.metric_value), 6) });
    fatigueBySet.set(r.ad_object_id, list);
  }
  for (const [id, list] of fatigueBySet) {
    const byDate = new Map(list.map(p => [p.date, p]));
    fatigueBySet.set(
      id,
      [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
    );
  }

  const fatiguedByAdSet = new Map(
    fatiguedAds
      .filter(r => r.granularity !== 'campaign')
      .map(r => {
        const obj: FatiguedAd = {
          adObjectId: r.ad_object_id,
          name: r.name || '',
          format: 'Static', // TODO replace w format when i find out how to get it
          formatCode:
            r.creative_format === null || r.creative_format === '' ? null : num(r.creative_format),
          spend: round(num(r.spend), 2),
          costPerResult:
            r.cost_per_result === null || r.cost_per_result === ''
              ? null
              : round(num(r.cost_per_result), 2),
          thumbnailUrl: r.thumbnail_url || null
        };
        return [obj.adObjectId, obj] as const;
      })
  );

  const statusByAdSet = new Map<string, AdSetStatus>();
  for (const r of adsetStatus) {
    statusByAdSet.set(r.adset_id, {
      status: r.adset_effective_status,
      effectiveStatus: r.adset_effective_status,
      active: (r.adset_effective_status || '').toUpperCase() === 'ACTIVE'
    });
  }

  interface AdAccum {
    adId: string;
    name: string;
    agg: Agg;
    firstSpendDate: string | null;
  }
  interface SetAccum {
    adSetId: string;
    name: string;
    campaign: string;
    objective: string;
    account: string;
    accountName: string;
    totals: Agg;
    ads: Map<string, AdAccum>;
    daily: Map<string, Agg>;
  }
  const sets = new Map<string, SetAccum>();

  // The backend already scopes adSetTotals to ad sets tracked in the diversity table
  // (see this page's CLAUDE.md) — but "tracked" means a row exists there, not that the
  // row has a USABLE score. An ad set with an 'n/a'/null-score row still has real
  // fatigue/performance data and belongs on the Fatigue tab, so every adSetTotals row
  // becomes a tracked set here. diversityBySet (LOW/MEDIUM/HIGH + a finite score) is
  // consulted below only to decide diversityLabel/diversityScore, which can end up null.
  for (const r of adSetTotals) {
    sets.set(r.adset_id, {
      adSetId: r.adset_id,
      name: r.adset_name,
      campaign: r.campaign_name,
      objective: r.objective,
      account: r.account,
      accountName: r.account_name,
      totals: {
        spend: num(r.spend),
        impressions: num(r.impressions),
        clicks: num(r.clicks)
      },
      ads: new Map(),
      daily: new Map()
    });
  }

  for (const r of adTotals) {
    const set = sets.get(r.adset_id);
    if (!set) continue; // no accompanying ad-set totals row for this window
    set.ads.set(r.ad_id, {
      adId: r.ad_id,
      name: r.ad_name,
      agg: {
        spend: num(r.spend),
        impressions: num(r.impressions),
        clicks: num(r.clicks)
      },
      firstSpendDate: r.first_spend_date
    });
  }

  let minDate: string | null = null;
  for (const r of adSetDailyPerf) {
    if (minDate === null || r.date < minDate) {
      minDate = r.date;
    }
    const set = sets.get(r.adset_id);
    if (!set) continue;
    set.daily.set(r.date, {
      spend: num(r.spend),
      impressions: num(r.impressions),
      clicks: num(r.clicks)
    });
  }

  // adKpiVolumeRevenue is keyed by ad_id only (no adset_id column), so route each ad's
  // embedded daily revenue/volume to its ad set via the roster built above.
  const adSetIdByAdId = new Map<string, string>();
  for (const [adSetId, set] of sets) {
    for (const adId of set.ads.keys()) {
      adSetIdByAdId.set(adId, adSetId);
    }
  }
  interface DailyKpi {
    revenue: number;
    volume: number;
  }
  const dailyKpiBySet = new Map<string, Map<string, DailyKpi>>();
  for (const r of adKpiVolumeRevenue) {
    const adSetId = adSetIdByAdId.get(r.ad_id);
    if (!adSetId) continue;
    const byDate = dailyKpiBySet.get(adSetId) ?? new Map<string, DailyKpi>();
    for (const day of r.dailyRevenue) {
      const prev = byDate.get(day.date) ?? { revenue: 0, volume: 0 };
      byDate.set(day.date, {
        revenue: prev.revenue + num(day.revenue),
        volume: prev.volume + num(day.volume)
      });
    }
    dailyKpiBySet.set(adSetId, byDate);
  }

  const campaigns = [...new Set([...sets.values()].map(s => s.campaign))].sort();
  const campIndex = new Map(campaigns.map((c, i) => [c, i]));

  const adSets: AdSet[] = [...sets.values()]
    .map(set => {
      // null here means the ad set's diversity row exists but is unusable (label 'n/a'
      // or a null/unparsable score) — or, defensively, no row at all. Either way it's a
      // real, distinct state, not a bug: the ad set still renders on the Fatigue/Combined
      // tabs; only the Diversity tab requires a real value (getDiversityAdSets in
      // meta-fatigue-insights-utils.ts).
      const div = diversityBySet.get(set.adSetId) ?? null;
      const totals = computeMetrics(set.totals);

      const ads: Ad[] = [...set.ads.values()]
        .map(ad => {
          const format = 'Static'; // TODO pt2
          const m = computeMetrics(ad.agg);
          const kpi = kpiByAdId.get(ad.adId) ?? null;
          return {
            adId: ad.adId,
            name: ad.name,
            format,
            spend: m.spend,
            impressions: m.impressions,
            clicks: m.clicks,
            roas: computeRoas(kpi ? kpi.revenue : null, m.spend),
            cpm: m.cpm,
            ctr: m.ctr,
            cpx: computeCpx(kpi ? kpi.volume : null, m.spend),
            cpc: m.cpc,
            kpiVolume: kpi ? round(kpi.volume, 2) : null,
            kpiRevenue: kpi ? round(kpi.revenue, 2) : null,
            spendShare: round(set.totals.spend > 0 ? ad.agg.spend / set.totals.spend : 0),
            // null = never spent, or left-censored at the window start (already running).
            launchDate:
              ad.firstSpendDate !== null && minDate !== null && ad.firstSpendDate > minDate
                ? ad.firstSpendDate
                : null
          };
        })
        .sort((a, b) => b.spend - a.spend);

      const launchesByDate = new Map<string, string[]>();
      for (const ad of ads) {
        if (ad.launchDate === null) continue;
        const ids = launchesByDate.get(ad.launchDate) ?? [];
        ids.push(ad.adId);
        launchesByDate.set(ad.launchDate, ids);
      }
      const launches: LaunchEvent[] = [...launchesByDate.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, adIds]) => ({ date, count: adIds.length, adIds }));

      // Concentration: how many top-spend creatives carry ≥80% of the set's spend. Shared
      // with the filter path (recomputeAdSetFromAds) so a pruned ad list is measured by
      // exactly the same rule as the full one.
      const { creativesDriving, spendShare } = computeConcentration(ads.map(ad => ad.spendShare));

      const fatigueSeries = fatigueBySet.get(set.adSetId) ?? [];
      const fatigueLatest = fatigueSeries.length
        ? fatigueSeries[fatigueSeries.length - 1].fatigue
        : null;

      const dailyKpi = dailyKpiBySet.get(set.adSetId) ?? new Map<string, DailyKpi>();
      const perfDaily: PerfDailyPoint[] = [...set.daily.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, agg]) => {
          const m = computeMetrics(agg);
          const kpiDay = dailyKpi.get(date) ?? null;
          const roas = computeRoas(kpiDay?.revenue ?? null, m.spend);
          const cpx = computeCpx(kpiDay?.volume ?? null, m.spend);
          return { date, spend: m.spend, roas, cpx, ctr: m.ctr, cpm: m.cpm };
        });

      const flagged = fatiguedByAdSet.get(set.adSetId) ?? null;

      const adsWithKpi = ads.filter(
        (ad): ad is Ad & { kpiVolume: number; kpiRevenue: number } => ad.kpiVolume !== null
      );
      const kpiVolume = adsWithKpi.length
        ? round(
            adsWithKpi.reduce((sum, ad) => sum + ad.kpiVolume, 0),
            2
          )
        : null;
      const kpiRevenue = adsWithKpi.length
        ? round(
            adsWithKpi.reduce((sum, ad) => sum + ad.kpiRevenue, 0),
            2
          )
        : null;

      return {
        adSetId: set.adSetId,
        name: set.name,
        campaign: set.campaign,
        campaignIndex: campIndex.get(set.campaign) ?? -1,
        objective: set.objective,
        account: set.account,
        accountName: set.accountName,
        ...totals,
        kpiVolume,
        kpiRevenue,
        roas: computeRoas(kpiRevenue, totals.spend),
        cpx: computeCpx(kpiVolume, totals.spend),
        diversityLabel: div?.label ?? null,
        diversityScore: div?.score ?? null,
        activeAds: ads.length,
        creativesDriving,
        spendShare,
        fatigueLatest,
        fatigueSeries,
        fatigueFlagged: Boolean(flagged),
        costPerResult: flagged ? flagged.costPerResult : null,
        platforms: platformsBySet.get(set.adSetId) ?? null,
        status: statusByAdSet.get(set.adSetId) ?? null,
        perfDaily,
        launches,
        ads
      };
    })
    .sort((a, b) => b.spend - a.spend);

  return { adSets, campaigns };
};
